<template>
  <div
    class="modal api-key-create-modal"
    :class="{ 'is-active': isActive }"
    @keyup.esc="$emit('close-modal')"
  >
    <div class="modal-background"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <div>
          <p class="modal-card-title">
            {{
              createdKey.data
                ? $t('api_keys.info.store_api_key')
                : $t('api_keys.info.create_api_key')
            }}
          </p>
        </div>
      </header>
      <section class="modal-card-body">
        <div
          v-if="createdKey.data"
          class="key-view has-text-centered"
          :class="{
            copied: createdKey.isKeyCopied,
            downloaded: createdKey.isDownloaded,
          }"
        >
          <p class="text-info">
            {{ $t('api_keys.modal.copy_key_recommendation') }}
          </p>
          <div class="copy-btn-container">
            <button
              class="button copy-api-key-button"
              :data-clipboard-text="createdKey.data.api_key"
            >
              {{ $t('api_keys.info.api_key') }}
            </button>
            <p class="help" v-if="createdKey.isKeyCopied">
              <i class="material-icons">check_circle</i>
              <span>{{ $t('api_keys.info.copied') }}</span>
            </p>
            <p class="help" v-else>
              <i class="material-icons">file_copy</i>
              <span>
                {{ $t('api_keys.info.click_to_copy') }}
              </span>
            </p>
          </div>
          <div class="download-container">
            <a
              :href="textContent"
              target="_blank"
              download="api_key.txt"
              @click="createdKey.isDownloaded = true"
              v-if="!createdKey.isDownloaded"
              class="button download-key-button"
              :class="{ copied: createdKey.isKeyCopied }"
              >{{ $t('api_keys.info.download') }}</a
            >
            <button class="button completed-button" v-else>
              <i class="material-icons">check_circle</i>
              {{ $t('api_keys.info.completed') }}
            </button>
            <button
              :disabled="!createdKey.isKeyCopied && !createdKey.isDownloaded"
              class="button close-modal-button primary"
              @click="$emit('close-modal')"
            >
              {{ $t('api_keys.info.close') }}
            </button>
          </div>
        </div>
        <div v-else>
          <form
            id="webhook-request-form"
            class="is-centered"
            @submit.prevent="submitRequest"
          >
            <div class="form-fields">
              <div class="field columns gray-bg">
                <label class="label column is-3 is-flex pad-lt-0" title="Name">
                  <span>{{ $t('api_keys.info.name') }}</span>
                  <div class="is-relative tooltip-container">
                    <i class="material-icons">&#xE88F;</i>
                    <div class="tooltip tooltip-bottom" role="tooltip">
                      <div class="tooltip-outer">
                        <div class="tooltip-arrow"></div>
                        <div class="tooltip-inner">
                          {{ $t('api_keys.tooltips.name') }}
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
                <div class="control column is-9 is-flex">
                  <div class="input-container">
                    <input
                      type="text"
                      id="name"
                      class="input is-shadowless"
                      autofocus
                      v-model="form.name"
                      placeholder="Type here"
                      @change="validateRequestData('name')"
                    />
                    <p class="help is-danger" v-if="errors.name">
                      {{ errors.name }}
                    </p>
                  </div>
                  <div class="validation-container">
                    <i
                      class="material-icons"
                      v-if="validations.name && !errors.name"
                      >check_circle</i
                    >
                  </div>
                </div>
              </div>

              <div class="field">
                <div class="columns select-scope-label-container">
                  <label class="label coulumn is-flex" title="Select scopes">
                    <span>{{ $t('api_keys.info.select_scopes') }}</span>
                    <div class="is-relative tooltip-container">
                      <i class="material-icons">&#xE88F;</i>
                      <div class="tooltip tooltip-bottom" role="tooltip">
                        <div class="tooltip-outer">
                          <div class="tooltip-arrow"></div>
                          <div class="tooltip-inner">
                            {{ $t('api_keys.tooltips.scopes') }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </label>
                </div>
                <div class="columns">
                  <div class="control column is-12">
                    <div class="scope-options-container">
                      <div
                        v-for="(scopeOption, index) in scopeOptions"
                        :key="index"
                        class="scope-container"
                      >
                        <div class="check-box-container">
                          <p-check
                            class="p-icon p-curve p-smooth is-blue"
                            :class="{
                              'is-scope-active': form.scopes[scopeOption.name],
                            }"
                            v-model="form.scopes[scopeOption.name]"
                            @change="validateRequestData('scopes')"
                          >
                            <i class="icon material-icons" slot="extra"
                              >&#xE876;</i
                            >
                            <span class="scope-option-label">{{
                              scopeOption.label
                            }}</span>
                          </p-check>
                        </div>
                        <div class="tooltip-container">
                          <i class="material-icons">&#xE88F;</i>
                          <div class="tooltip tooltip-top" role="tooltip">
                            <div class="tooltip-outer">
                              <div class="tooltip-arrow"></div>
                              <div class="tooltip-inner">
                                {{
                                  $t(
                                    `api_keys.tooltips.${scopeOption.name.replace(
                                      '.',
                                      '_'
                                    )}_scope`
                                  )
                                }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p class="help is-danger clearfix" v-if="errors.scopes">
                      {{ errors.scopes }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="field know-more-container">
                <div class="columns">
                  <p class="help">
                    <span>
                      {{ $t('api_keys.modal.refer_documentation') }}
                    </span>
                    <a
                      href="https://docs.sentiance.com/backend/authentication-and-authorization#api-keys"
                      class="doc-link"
                    >
                      {{ $t('api_keys.modal.documentation') }}
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <div class="modal-card-footer is-flex">
              <button
                type="button"
                class="button cancel-btn"
                @click="$emit('close-modal')"
              >
                {{ $t('api_keys.info.cancel') }}
              </button>
              <button
                class="button primary submit-request-btn"
                :disabled="isDisabled"
              >
                {{ $t('api_keys.info.create') }}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import Ajv from 'ajv';

import Clipboard from 'clipboard/dist/clipboard';
import store from 'store';
import { mapState, mapGetters } from 'vuex';
import { pick } from 'lodash';

import Api from 'services/Api';
import i18n from 'services/i18n';
import notifications from 'services/Notifications';

const { keys, values } = Object;
const ajv = new Ajv({
  allErrors: true,
});
require('ajv-errors')(ajv);

ajv.addKeyword('selectAtleastOneScope', {
  validate: function validate(data, dataObj) {
    if (!values(dataObj).some(val => val)) {
      // To add new errors push the error object to the function's errors property

      validate.errors = ajv.errors || [];

      if (
        !validate.errors.find(
          val =>
            val.keyword === 'selectAtleastOneScope' ||
            val.schemaPath.includes('selectAtleastOneScope')
        )
      ) {
        validate.errors.push({
          keyword: 'selectAtleastOneScope',
          message: i18n.t('api_keys.errors.select_atleast_one_scope'),
          params: {
            selectAtleastOneScope: false,
          },
        });
      }
      return false;
    }

    return true;
  },
});

export default {
  name: 'KeyCreateModal',
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('application', ['app']),
    ...mapState('api_key', ['formValidator', 'validations', 'createdKey']),
    ...mapState('api_key', {
      form: 'createForm',
      errors: 'formErrors',
    }),
    textContent() {
      return (
        'data:text/plain;charset=utf-8,' +
        encodeURIComponent(this.createdKey.data.api_key)
      );
    },

    isDisabled() {
      const { values } = Object;

      return (
        this.isLoading ||
        values(this.validations).some(val => !val) ||
        values(this.errors).some(val => val)
      );
    },
  },
  mounted() {
    const clipboard = new Clipboard('button.copy-api-key-button');
    clipboard.on('success', data => {
      store.commit('api_key/setCreatedKey', { isKeyCopied: true });
    });
    clipboard.on('error', data =>
      notifications.add(`Failed to copy api Key`, 'warning')
    );
  },
  components: {},
  data() {
    return {
      isLoading: false,
      scopeOptions: [
        {
          name: 'user.read',
          label: 'User Read',
        },
        {
          name: 'user.delete',
          label: 'User Delete',
        },
        {
          name: 'user.link',
          label: 'User Link',
        },
      ],
    };
  },

  methods: {
    setValidations(dataPaths = '', field) {
      if (dataPaths && dataPaths.includes(field)) {
        this.validations[field] = false;
      } else {
        this.validations[field] = true;
        this.errors[field] = null;
      }
    },

    validateRequestData(field) {
      ['name'].forEach(val => {
        this.form[val] = this.form[val].trim();
      });

      ajv.validate(this.formValidator, this.form);
      const dataPaths =
        ajv.errors && ajv.errors.map(val => val.dataPath).join(' ');

      // Check and set the validations object
      if (field) {
        this.setValidations(dataPaths, field);
      } else {
        Object.keys(this.validations).forEach(val => {
          this.setValidations(dataPaths, val);
        });
      }

      // Check and set the validations error object
      ajv.errors &&
        ajv.errors.forEach(val => {
          const splitDataPath = val.dataPath.split('/');
          const key = splitDataPath[splitDataPath.length - 1];
          if (field) {
            if (key === field) {
              this.errors[key] = val.message;
            }
          } else {
            this.errors[key] = val.message;
          }
        });
    },

    async submitRequest(event) {
      try {
        this.validateRequestData();

        if (values(this.errors).some(val => val)) {
          return false;
        }

        const params = {
          name: this.form.name,
          scopes: keys(this.form.scopes).filter(val => this.form.scopes[val]),
        };
        this.isLoading = true;
        const response = await store.dispatch(
          'api_key/createKeyForCurrentAppId',
          params
        );

        if (response.status < 300) {
          if (response.body && response.body.id) {
            store.commit('api_key/setCreatedKey', { data: response.body });
            store.commit('api_key/resetForm');
            // Update the store with new data
            this.$emit('reload');
          }
        } else {
          throw response;
        }
      } catch (error) {
        console.log(error);

        const message = error.body && error.body.message;
        notifications.add(
          message || this.$t('api_keys.errors.create_key_failed')
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
